import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';
import { Loader2, Link2, UserPlus, Search, Check } from 'lucide-react';

export const LinkCustomerDialog = ({ open, onOpenChange, conv, onLinked }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [saveNumber, setSaveNumber] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setQuery(''); setResults([]); setSelected(null); setSaveNumber(true); }
  }, [open, conv?.id]);

  const search = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const { data } = await api.get(`/customers?search=${encodeURIComponent(q)}&limit=10`);
      setResults(Array.isArray(data) ? data : (data.customers || []));
    } catch { setResults([]); } finally { setSearching(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => search(query), 300);
    return () => clearTimeout(t);
  }, [query, search]);

  const submit = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/whatsapp/conversations/${conv.id}/link`, {
        customer_id: selected.id,
        save_number: saveNumber,
        contact_name: '',
      });
      toast.success(`Linked to ${data.customer_name}`);
      onLinked?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not link');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="link-customer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Link2 className="w-4 h-4" /> Link to customer</DialogTitle>
          <DialogDescription>
            Connect {conv?.phone ? <span className="font-medium">{conv.phone}</span> : 'this conversation'} to a customer record.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              autoFocus
              data-testid="link-customer-search"
              className="pl-9"
              placeholder="Search by name, email or phone…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <ScrollArea className="max-h-56 border rounded-md">
            {searching ? (
              <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
            ) : results.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">{query.length < 2 ? 'Type to search customers' : 'No matches'}</p>
            ) : (
              results.map((c) => {
                const name = `${c.first_name || ''} ${c.last_name || ''}`.trim();
                const isSel = selected?.id === c.id;
                return (
                  <button
                    key={c.id}
                    data-testid={`link-customer-option-${c.id}`}
                    onClick={() => setSelected(c)}
                    className={`w-full text-left px-3 py-2 border-b last:border-0 hover:bg-muted/50 flex items-center justify-between ${isSel ? 'bg-emerald-50' : ''}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{name || c.email}</p>
                      <p className="text-xs text-muted-foreground truncate">{c.phone || c.email || ''}</p>
                    </div>
                    {isSel && <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />}
                  </button>
                );
              })
            )}
          </ScrollArea>

          {conv?.phone && (
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <Checkbox checked={saveNumber} onCheckedChange={(v) => setSaveNumber(!!v)} data-testid="link-save-number" />
              Save {conv.phone} to this customer's contact details
            </label>
          )}

          <Button onClick={submit} disabled={!selected || saving} className="w-full" data-testid="link-customer-submit">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
            Link conversation
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
